/**
 * Video Analysis Modal (Phase 12.2)
 *
 * Open-ended manual trigger for video analysis.
 * Only time range + camera are required. SOP, operator, and notes are optional.
 * Adapts Gemini analysis based on available context.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Video,
  Camera,
  User,
  FileText,
  MessageSquare,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Scan,
  Gauge,
} from 'lucide-react';
import { useCreateFromVideo } from '../../../hooks/useAssessments';
import { useCameras } from '../../../hooks/useCameras';
import { useOperatorLookup } from '../../../hooks/useOperators';
import { useSopLookup, useSopSectionsLookup } from '../../../hooks/useSOPs';
import { useKpiLookup } from '../../../hooks/useKpi';
import { ScopeChipPicker } from '../../../components/kpi/ScopeChipPicker';

interface VideoAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Phase 36.2: optional pre-fill for the ask bar's Analyze Video hand-off.
   *
   * Consumed ONLY in the `useState` initializers below, so omitting it (which
   * `AssessmentsPage.tsx` does) leaves behaviour byte-identical to before this
   * prop existed. SOP and Jobs are deliberately NOT pre-fillable: a
   * natural-language bar cannot supply a Job selection, and an unscoped
   * analysis is exactly the whole-SOP path Phase 36.4 spent eight chunks
   * eliminating. The user picks those here, and the write goes through this
   * modal's own submit with its own `scope ⊆ SOP ⊆ org` validation.
   *
   * `cameraId` is `camera_configurations.id` — what the `<select>` holds. The
   * remap to the Alta device GUID happens at submit time (`:126`), so passing
   * the GUID here yields a silently unselected dropdown.
   *
   * Initial values only. The caller must mount this conditionally (the ask bar
   * does) so a new pre-fill gets a fresh mount — `isOpen: false` is an early
   * return here, not an unmount, and state survives it.
   */
  initialValues?: {
    startTime?: string;
    endTime?: string;
    cameraId?: string;
    sourceNumber?: number;
  };
}

export function VideoAnalysisModal({ isOpen, onClose, initialValues }: VideoAnalysisModalProps) {
  const navigate = useNavigate();
  const createFromVideo = useCreateFromVideo();
  const { data: cameras } = useCameras();
  const { data: operators } = useOperatorLookup();
  const { data: sops } = useSopLookup();
  const { data: kpiLookup } = useKpiLookup();

  // Form state
  // Phase 36.2: the `?? <previous literal>` fallbacks are the no-regression
  // contract — with no `initialValues` every initializer is exactly what it was
  // before the prop existed.
  const [startTime, setStartTime] = useState(initialValues?.startTime ?? '');
  const [endTime, setEndTime] = useState(initialValues?.endTime ?? '');
  const [cameraId, setCameraId] = useState(initialValues?.cameraId ?? '');
  // Phase 35.5: target lens for multi-sensor cameras (default = Lens 1)
  const [sourceNumber, setSourceNumber] = useState<number>(initialValues?.sourceNumber ?? 1);
  const [operatorId, setOperatorId] = useState('');
  const [sopId, setSopId] = useState('');
  // Phase 36.4 v3: Jobs of the selected SOP to scope this analysis to. Empty =
  // whole SOP, i.e. every request before this feature existed. Held as [] rather
  // than null because ScopeChipPicker takes string[]; the empty -> omitted
  // conversion happens once, in the submit payload.
  const [scopeSectionIds, setScopeSectionIds] = useState<string[]>([]);
  const [kpiDefinitionId, setKpiDefinitionId] = useState('');
  const [notes, setNotes] = useState('');
  const [triggerId, setTriggerId] = useState<string | null>(null);

  // Phase 35.5: the selected camera's sourceCount drives the lens dropdown
  const selectedCamera = cameras?.find((c) => c.id === cameraId);

  // Phase 36.4 v3: the Jobs of the selected SOP. Disabled until a SOP is picked
  // — without one there is nothing for a scope to be a subset of, and the API
  // 400s a scope sent with no sopId.
  const sectionsLookup = useSopSectionsLookup(sopId || null);

  // Unlike the Templates form there is no stored scope to merge stale ids back
  // into: every id in `scopeSectionIds` was just picked from this same lookup,
  // so an unknown id is not reachable here.
  const jobOptions = useMemo(
    () =>
      (sectionsLookup.data ?? []).map((r) => ({
        id: r.id,
        // UI terminology: sopSections render as "Jobs" (critical-patterns.md).
        // The "no steps" suffix pre-empts the API's "have no steps and cannot be
        // scored" 400 — the modal's error banner is generic, so an unexplained
        // rejection here would be a dead end.
        name:
          r.stepCount === 0
            ? `Job ${r.sectionNumber}: ${r.title} — no steps`
            : `Job ${r.sectionNumber}: ${r.title}`,
      })),
    [sectionsLookup.data]
  );

  const selectedWithoutSteps = (sectionsLookup.data ?? []).filter(
    (r) => r.stepCount === 0 && scopeSectionIds.includes(r.id)
  );

  const isSubmitting = createFromVideo.isPending;
  const isSuccess = createFromVideo.isSuccess;
  const isError = createFromVideo.isError;

  // Validate time range
  const getTimeRangeError = () => {
    if (!startTime || !endTime) return null;
    const start = new Date(startTime);
    const end = new Date(endTime);
    if (end <= start) return 'End time must be after start time';
    const durationMs = end.getTime() - start.getTime();
    if (durationMs > 2 * 60 * 60 * 1000) return 'Time range cannot exceed 2 hours';
    return null;
  };

  const timeError = getTimeRangeError();
  const canSubmit = startTime && endTime && cameraId && !timeError && !isSubmitting;

  // Determine analysis mode for UI hint
  const analysisMode = sopId
    ? kpiDefinitionId
      ? notes ? 'SOP + KPI + Manager Question' : 'SOP + KPI Assessment'
      : notes ? 'SOP Assessment + Manager Question' : 'SOP Compliance Assessment'
    : kpiDefinitionId
      ? notes ? 'KPI Analysis + Manager Question' : 'KPI Analysis'
      : notes
        ? 'Observation — Answer Question'
        : 'General Observation';

  const handleSubmit = async () => {
    if (!canSubmit) return;

    try {
      // Find the Alta device ID from the camera config
      const altaDeviceId = selectedCamera?.cameraId ?? cameraId;

      const result = await createFromVideo.mutateAsync({
        startTime: new Date(startTime).toISOString(),
        endTime: new Date(endTime).toISOString(),
        cameraId: altaDeviceId,
        // Phase 35.5: only sent for a multi-lens camera with a non-default
        // pick — absent = lens 1, byte-identical to pre-35.5 behavior
        sourceNumber:
          selectedCamera && selectedCamera.sourceCount > 1 && sourceNumber > 1
            ? sourceNumber
            : undefined,
        operatorId: operatorId || undefined,
        sopId: sopId || undefined,
        // Phase 36.4 v3: only sent when a SOP is chosen AND Jobs were picked.
        // Omitted otherwise, so an unscoped request is byte-identical to the
        // pre-36.4-v3 body. No "lookup still loading" arm: unlike the Templates
        // form there is no stored scope that a premature value could clobber.
        scopeSectionIds:
          sopId && scopeSectionIds.length > 0 ? scopeSectionIds : undefined,
        kpiDefinitionId: kpiDefinitionId || undefined,
        notes: notes || undefined,
      });

      setTriggerId((result as { triggerId: string }).triggerId);
    } catch {
      // Error handled by mutation state
    }
  };

  const handleReset = () => {
    setStartTime('');
    setEndTime('');
    setCameraId('');
    setSourceNumber(1); // Phase 35.5
    setOperatorId('');
    setSopId('');
    setScopeSectionIds([]); // Phase 36.4 v3
    setKpiDefinitionId('');
    setNotes('');
    setTriggerId(null);
    createFromVideo.reset();
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-guild-green/5 to-transparent">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-guild-green/10">
              <Scan size={18} className="text-guild-green" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Video Analysis</h2>
              <p className="text-xs text-gray-500">Analyze camera footage with AI</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Success State */}
        {isSuccess && triggerId ? (
          <div className="p-6 text-center space-y-4">
            <div className="flex justify-center">
              <div className="h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 size={28} className="text-emerald-600" />
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Analysis Queued</h3>
              <p className="text-sm text-gray-500 mt-1">
                {sopId ? 'Assessment' : kpiDefinitionId ? 'KPI analysis' : 'Observation'} is being processed. You'll be notified when complete.
              </p>
            </div>
            <div className="flex gap-3 justify-center pt-2">
              <button
                onClick={() => navigate(`/assessments/triggers/${triggerId}`)}
                className="inline-flex items-center gap-2 rounded-xl bg-guild-green px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-guild-green/25 hover:bg-guild-green/90 transition-all"
              >
                View Progress
              </button>
              <button
                onClick={handleClose}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Form */}
            <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Time Range */}
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <Video size={15} className="text-guild-green" />
                  Time Range <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Start</label>
                    <input
                      type="datetime-local"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-guild-green focus:ring-1 focus:ring-guild-green/30 outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">End</label>
                    <input
                      type="datetime-local"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-guild-green focus:ring-1 focus:ring-guild-green/30 outline-none"
                    />
                  </div>
                </div>
                {timeError && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle size={12} /> {timeError}
                  </p>
                )}
              </div>

              {/* Camera */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <Camera size={15} className="text-guild-green" />
                  Camera <span className="text-red-500">*</span>
                </label>
                <select
                  value={cameraId}
                  onChange={(e) => {
                    setCameraId(e.target.value);
                    setSourceNumber(1); // Phase 35.5: lens picks don't carry across cameras
                  }}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-guild-green focus:ring-1 focus:ring-guild-green/30 outline-none"
                >
                  <option value="">Select camera...</option>
                  {cameras?.filter((c) => c.isActive).map((camera) => (
                    <option key={camera.id} value={camera.id}>
                      {camera.name} {camera.location ? `(${camera.location})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Phase 35.5: Camera Lens — only shown for multi-sensor cameras (default Lens 1) */}
              {selectedCamera && selectedCamera.sourceCount > 1 && (
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <Camera size={15} className="text-guild-green" />
                    Camera Lens
                  </label>
                  <select
                    value={sourceNumber}
                    onChange={(e) => setSourceNumber(parseInt(e.target.value))}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-guild-green focus:ring-1 focus:ring-guild-green/30 outline-none"
                  >
                    {Array.from({ length: selectedCamera.sourceCount }, (_, i) => i + 1).map((lens) => (
                      <option key={lens} value={lens}>
                        Lens {lens}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500">
                    This camera records {selectedCamera.sourceCount} lenses. Analysis will use the selected lens.
                  </p>
                </div>
              )}

              {/* Divider with "Optional" label */}
              <div className="relative py-1">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-100" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-3 text-xs text-gray-400 font-medium">Optional</span>
                </div>
              </div>

              {/* Operator */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <User size={15} className="text-gray-400" />
                  Operator
                </label>
                <select
                  value={operatorId}
                  onChange={(e) => setOperatorId(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-guild-green focus:ring-1 focus:ring-guild-green/30 outline-none text-gray-600"
                >
                  <option value="">Auto-detect from video</option>
                  {operators?.map((op) => (
                    <option key={op.id} value={op.id}>
                      {op.fullName}
                    </option>
                  ))}
                </select>
              </div>

              {/* SOP */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <FileText size={15} className="text-gray-400" />
                  SOP
                </label>
                <select
                  value={sopId}
                  // Phase 36.4 v3: clearing the Jobs on a SOP change is
                  // mandatory, not tidiness — the API validates the scope as a
                  // subset of the chosen SOP, so carrying the old Jobs over
                  // would 400 with "do not belong to this SOP". Mirrors the
                  // camera -> lens reset above.
                  onChange={(e) => {
                    setSopId(e.target.value);
                    setScopeSectionIds([]);
                  }}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-guild-green focus:ring-1 focus:ring-guild-green/30 outline-none text-gray-600"
                >
                  <option value="">No SOP — observation only</option>
                  {sops?.map((sop) => (
                    <option key={sop.id} value={sop.id}>
                      {sop.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Phase 36.4 v3: Jobs — only once a SOP is picked. Without a SOP
                  there is nothing for the scope to be a subset of, and the API
                  rejects a scope sent on its own. */}
              {!!sopId && (
                <div className="space-y-2">
                  <ScopeChipPicker
                    label="Jobs"
                    options={jobOptions}
                    selectedIds={scopeSectionIds}
                    onChange={setScopeSectionIds}
                    required={false}
                    placeholder={
                      sectionsLookup.isLoading
                        ? 'Loading Jobs…'
                        : jobOptions.length === 0
                          ? 'No Jobs yet'
                          : 'Select Jobs…'
                    }
                    helpText="Optional — empty scores every Job"
                    disabled={isSubmitting}
                  />
                  <p className="text-xs text-gray-500">
                    Naming the Jobs you want reviewed tells the analysis which
                    processes to look for. Leave empty to consider every Job in the
                    SOP.
                  </p>
                  {sectionsLookup.error && (
                    <p className="flex items-center gap-1 text-xs text-red-500">
                      <AlertCircle size={12} /> Couldn't load this SOP's Jobs — reselect
                      the SOP to retry.
                    </p>
                  )}
                  {selectedWithoutSteps.length > 0 && (
                    <p className="text-xs text-amber-600">
                      {selectedWithoutSteps
                        .map((r) => `Job ${r.sectionNumber}: ${r.title}`)
                        .join(', ')}{' '}
                      {selectedWithoutSteps.length === 1 ? 'has' : 'have'} no steps and
                      cannot be scored. Remove{' '}
                      {selectedWithoutSteps.length === 1 ? 'it' : 'them'} to continue.
                    </p>
                  )}
                </div>
              )}

              {/* KPI (Phase 24.5) */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <Gauge size={15} className="text-gray-400" />
                  KPI
                </label>
                <select
                  value={kpiDefinitionId}
                  onChange={(e) => setKpiDefinitionId(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-guild-green focus:ring-1 focus:ring-guild-green/30 outline-none text-gray-600"
                >
                  <option value="">None — skip KPI analysis</option>
                  {kpiLookup?.map((kpi) => (
                    <option key={kpi.id} value={kpi.id}>
                      {kpi.name}{kpi.category ? ` (${kpi.category})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <MessageSquare size={15} className="text-gray-400" />
                  Notes / Question
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder='Ask a question about the video... e.g. "Did anyone clean the rails?"'
                  rows={3}
                  maxLength={2000}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-guild-green focus:ring-1 focus:ring-guild-green/30 outline-none resize-none placeholder:text-gray-400"
                />
                {notes && (
                  <p className="text-xs text-gray-400 text-right">{notes.length}/2000</p>
                )}
              </div>

              {/* Analysis Mode Indicator */}
              <div className="rounded-lg bg-gray-50 border border-gray-100 px-4 py-3">
                <p className="text-xs font-medium text-gray-500">Analysis mode</p>
                <p className="text-sm font-semibold text-gray-800 mt-0.5">{analysisMode}</p>
              </div>

              {/* Error */}
              {isError && (
                <div className="rounded-lg bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-700">
                  Failed to queue analysis. Please try again.
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <button
                onClick={handleClose}
                className="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="
                  inline-flex items-center gap-2 rounded-xl
                  bg-guild-green px-5 py-2.5
                  text-sm font-semibold text-white
                  shadow-lg shadow-guild-green/25
                  transition-all duration-200
                  hover:bg-guild-green/90 hover:shadow-xl hover:shadow-guild-green/30
                  active:scale-[0.98]
                  disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none
                "
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Queuing...
                  </>
                ) : (
                  <>
                    <Scan size={16} />
                    Analyze Video
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
